import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, ShieldCheck } from "lucide-react";
import { company } from "../mock";

const nav = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/pricing", label: "Pricing" },
  { to: "/audit-readiness", label: "Audit Readiness" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all ${
        scrolled
          ? "bg-white/95 backdrop-blur border-b border-slate-200 shadow-sm"
          : "bg-white border-b border-slate-100"
      }`}
    >
      <div className="hidden md:block bg-navy text-white text-xs">
        <div className="container-x flex items-center justify-between py-2">
          <span className="flex items-center gap-2 text-teal-light">
            <ShieldCheck size={14} /> Practical NDIS Registration, Compliance and Audit Readiness Support
            <span className="text-slate-400 mx-2">|</span>
            <span className="inline-flex items-center gap-1.5" data-testid="topbar-phone">
              <Phone size={12} />
              <a href={`tel:${company.phoneRaw}`} className="hover:text-white transition" data-testid="topbar-phone-1">{company.phone}</a>
              <span className="text-slate-400">|</span>
              <a href={`tel:${company.phoneRaw2}`} className="hover:text-white transition" data-testid="topbar-phone-2">{company.phone2}</a>
            </span>
          </span>
          <Link to="/contact" className="hover:text-teal-light transition font-medium" data-testid="topbar-book-cta">
            Book Discovery Call
          </Link>
        </div>
      </div>

      <div className="container-x flex items-center justify-between py-6">
        <Link to="/" className="flex items-center group" data-testid="brand-logo-link">
          <img
            src={company.logoUrl}
            alt="Apex Quality Consultancy"
            className="h-12 md:h-14 lg:h-16 w-auto object-contain"
            style={{ background: "transparent", boxShadow: "none", border: "none" }}
          />
        </Link>

        <nav className="hidden xl:flex items-center gap-1" data-testid="primary-nav">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-3 py-2 text-[14.5px] font-medium transition ${
                  isActive ? "text-teal" : "text-navy/85 hover:text-teal"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-2.5">
          <Link to="/contact" className="btn-primary px-5 py-3 rounded-lg text-sm font-semibold" data-testid="header-book-discovery-call">
            Book Discovery Call
          </Link>
        </div>

        <button className="xl:hidden p-2 text-navy" onClick={() => setOpen(!open)} aria-label="Toggle menu" data-testid="mobile-menu-toggle">
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="xl:hidden border-t border-slate-200 bg-white max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
          <div className="container-x py-4 flex flex-col gap-1">
            {nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="py-2.5 text-navy font-medium hover:text-teal"
              >
                {item.label}
              </NavLink>
            ))}
            <div className="flex flex-col gap-2 mt-3">
              <a href={`tel:${company.phoneRaw}`} className="flex items-center gap-2 py-2 text-navy font-semibold" data-testid="mobile-phone-1">
                <Phone size={16} className="text-teal" /> {company.phone}
              </a>
              <a href={`tel:${company.phoneRaw2}`} className="flex items-center gap-2 py-2 text-navy font-semibold" data-testid="mobile-phone-2">
                <Phone size={16} className="text-teal" /> {company.phone2}
              </a>
              <Link to="/contact" className="btn-primary px-4 py-2.5 rounded-lg text-sm font-semibold text-center mt-1" data-testid="mobile-book-discovery-call">
                Book Discovery Call
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
